/**
 * SFD Writer Plugin — writes sf: namespace properties to BPMN elements.
 * Registered via epxFlow.addWriterExtension(plugin).
 *
 * Matches sfd-v1 output format exactly:
 *  - guid/code/url/version     → direct attrs on all elements (Identity extends bpmn:BaseElement)
 *  - from/to/priority/...      → direct attrs on SequenceFlow (GroupBehaviours extends bpmn:SequenceFlow)
 *  - trigger/expression        → direct attrs on StartEvent/CatchEvent (TriggerDetail extends those types)
 *  - automaticAssignment/...   → direct attrs on Task/UserTask (AdminProcessDetail extends bpmn:Task)
 *  - linkDocumentExpedient/... → direct attrs on Task/UserTask (DocumentManagementDetail extends bpmn:Task)
 *  - complexType/...           → direct attrs on Task/UserTask (MultiSignDetail extends bpmn:Task)
 *  - extraSplitType/...        → direct attrs on Gateways (GatewayDetail extends gateways)
 *  - Services, Scripts         → <sf:services>/<sf:scripts> extensionElements
 *  - Actions, Performers       → extensionElements (list containers)
 *  - Sections                  → <sf:sections><sf:section name="...">body</sf:section></sf:sections>
 *  - Boundaries, Notifications, SubInfoes → extensionElements
 */

// sf: attributes written directly on the element (Identity only)
const SF_IDENTITY_ATTRS = [
  'guid', 'code', 'url',
];

// AdminProcessDetail — written as direct attrs on Task/UserTask via extends
const ADMIN_PROCESS_PROPS = [
  'automaticAssignment', 'approvalRequired', 'sendBackActivity',
  'electDocRequired', 'publishDocument', 'publishAttachments', 'publishRelatedDocument',
];

// DocumentManagementDetail — direct attrs on Task/UserTask
const DOC_MGMT_PROPS = [
  'assignTo', 'linkDocumentExpedient', 'linkAttachmentExpedient', 'sendersAssignment',
];

// MultiSignDetail — direct attrs on Task/UserTask
const MULTI_SIGN_PROPS = [
  'complexType', 'mergeType', 'compareType', 'completeOrder',
];

// BusinessRuleDetail — direct attrs on BusinessRuleTask
const BUSINESS_RULE_PROPS = ['decisionRef', 'resultVariable'];

// GatewayDetail — direct attrs on Gateways
const GATEWAY_PROPS = [
  'extraSplitType', 'extraJoinType', 'joinPassType',
];

// GroupBehaviours — direct attrs on SequenceFlow via extends
const GROUP_BEHAVIOUR_PROPS = [
  'priority', 'forcedMerge', 'defaultBranch', 'approval', 'recieverType',
];

// Boundary overdue — direct attrs
const BOUNDARY_ATTRS = ['overdue', 'overdueType'];

/** Returns true if any of the listed props have a non-empty value */
function hasAny(props, keys) {
  return keys.some(k => props[k] !== undefined && props[k] !== null && props[k] !== '');
}

/** Builds attrs object from props for the given keys (only non-empty values) */
function pickAttrs(props, keys) {
  const attrs = {};
  for (const k of keys) {
    if (props[k] !== undefined && props[k] !== null && props[k] !== '') {
      attrs[k] = String(props[k]);
    }
  }
  return attrs;
}

export class SfdWriterPlugin {
  applyProperties(el, props, moddle) {
    // ── Identity: direct attrs ────────────────────────────────────────────
    for (const attr of SF_IDENTITY_ATTRS) {
      if (props[attr] !== undefined && props[attr] !== null && props[attr] !== '') {
        el[attr] = String(props[attr]);
      }
    }

    // ── AdminProcessDetail: direct attrs on Task/UserTask ─────────────────
    for (const attr of ADMIN_PROCESS_PROPS) {
      if (props[attr] !== undefined && props[attr] !== null && props[attr] !== '') {
        el[attr] = String(props[attr]);
      }
    }

    // ── DocumentManagementDetail: direct attrs on Task/UserTask ───────────
    for (const attr of DOC_MGMT_PROPS) {
      if (props[attr] !== undefined && props[attr] !== null && props[attr] !== '') {
        el[attr] = String(props[attr]);
      }
    }

    // ── MultiSignDetail: direct attrs on Task/UserTask ────────────────────
    for (const attr of MULTI_SIGN_PROPS) {
      if (props[attr] !== undefined && props[attr] !== null && props[attr] !== '') {
        el[attr] = String(props[attr]);
      }
    }

    // ── BusinessRuleDetail: direct attrs on BusinessRuleTask ─────────────
    for (const attr of BUSINESS_RULE_PROPS) {
      if (props[attr] !== undefined && props[attr] !== null && props[attr] !== '') {
        el[attr] = String(props[attr]);
      }
    }

    // ── sf:DecisionTable: DMN XML body on BusinessRuleTask ───────────────
    const dmnXml = props.dmnXml;
    if (dmnXml && typeof dmnXml === 'string' && dmnXml.trim() !== '') {
      if (!el.extensionElements) {
        el.extensionElements = moddle.create('bpmn:ExtensionElements', { values: [] });
      }
      el.extensionElements.values.push(
        moddle.create('sf:DecisionTable', { xml: dmnXml })
      );
    }

    // ── GatewayDetail: direct attrs on Gateways ───────────────────────────
    for (const attr of GATEWAY_PROPS) {
      if (props[attr] !== undefined && props[attr] !== null && props[attr] !== '') {
        el[attr] = String(props[attr]);
      }
    }

    // ── TriggerDetail: sf:trigger + sf:expression as direct attrs ─────────
    // TriggerDetail extends bpmn:StartEvent/IntermediateCatchEvent in sf.json
    if (props.triggerType !== undefined && props.triggerType !== null && props.triggerType !== '') {
      el.trigger = String(props.triggerType);
      const expression = props.timerExpression || props.conditionExpression
        || props.messageRef || props.signalRef || props.expressionText;
      if (expression) {
        el.expression = String(expression);
      }
    }

    // ── Boundary overdue: direct attrs ────────────────────────────────────
    for (const attr of BOUNDARY_ATTRS) {
      if (props[attr] !== undefined && props[attr] !== null && props[attr] !== '') {
        el[attr] = String(props[attr]);
      }
    }

    const extensions = [];

    // ── sf:Actions ────────────────────────────────────────────────────────
    if (props.actions && props.actions.length) {
      const sfActions = moddle.create('sf:Actions', {
        actions: props.actions.map((a) => {
          const attrs = {};
          for (const key of [
            'fireType', 'methodType', 'subMethodType', 'argus', 'expression',
            'emailBody', 'emailCC', 'emailSubject', 'emailTo',
            'typeFullName', 'assembly', 'methodName',
          ]) {
            if (a[key] !== undefined && a[key] !== null && a[key] !== '') attrs[key] = a[key];
          }
          return moddle.create('sf:Action', attrs);
        }),
      });
      extensions.push(sfActions);
    }

    // ── sf:Performers ─────────────────────────────────────────────────────
    if (props.performers && props.performers.length) {
      const sfPerformers = moddle.create('sf:Performers', {
        values: props.performers.map((p) => {
          const attrs = {};
          for (const key of ['name', 'outerId', 'outerCode', 'outerType', 'roleLevel']) {
            if (p[key] !== undefined && p[key] !== null && p[key] !== '') attrs[key] = p[key];
          }
          return moddle.create('sf:Performer', attrs);
        }),
      });
      extensions.push(sfPerformers);
    }

    // ── sf:Services ───────────────────────────────────────────────────────
    // Written as <sf:services><sf:service methodType="..." .../></sf:services>
    if (hasAny(props, ['serviceMethodType', 'serviceSubMethodType', 'serviceArgus', 'serviceExpression'])) {
      const svcAttrs = {};
      if (props.serviceMethodType) svcAttrs.methodType = props.serviceMethodType;
      if (props.serviceSubMethodType) svcAttrs.subMethodType = props.serviceSubMethodType;
      if (props.serviceArgus) svcAttrs.argus = props.serviceArgus;
      if (props.serviceExpression) svcAttrs.expression = props.serviceExpression;
      const sfServices = moddle.create('sf:Services', {
        services: [moddle.create('sf:Service', svcAttrs)],
      });
      extensions.push(sfServices);
    }

    // ── sf:Scripts ────────────────────────────────────────────────────────
    // Written as <sf:scripts><sf:script scriptType="..." argus="...">body</sf:script></sf:scripts>
    if (hasAny(props, ['scriptType', 'scriptArgus', 'scriptText'])) {
      const scrAttrs = {};
      if (props.scriptType) scrAttrs.scriptType = props.scriptType;
      if (props.scriptArgus) scrAttrs.argus = props.scriptArgus;
      if (props.scriptText) scrAttrs.scriptText = props.scriptText;
      const sfScripts = moddle.create('sf:Scripts', {
        scripts: [moddle.create('sf:Script', scrAttrs)],
      });
      extensions.push(sfScripts);
    }

    // ── sf:Sections ───────────────────────────────────────────────────────
    // Written as <sf:sections><sf:section name="...">body text</sf:section></sf:sections>
    if (props.sectionText || props.sectionName) {
      const sectionAttrs = {};
      if (props.sectionName) sectionAttrs.name = props.sectionName;
      if (props.sectionText) sectionAttrs.text = props.sectionText;
      const sfSections = moddle.create('sf:Sections', {
        sections: [moddle.create('sf:Section', sectionAttrs)],
      });
      extensions.push(sfSections);
    }

    // ── sf:Boundaries ─────────────────────────────────────────────────────
    if (props.boundaries && props.boundaries.length) {
      const sfBoundaries = moddle.create('sf:Boundaries', {
        boundaries: props.boundaries.map((b) => {
          const attrs = {};
          if (b.event) attrs.event = b.event;
          if (b.expression) attrs.expression = b.expression;
          return moddle.create('sf:Boundary', attrs);
        }),
      });
      extensions.push(sfBoundaries);
    }

    // ── sf:Notifications ──────────────────────────────────────────────────
    // notificationText is isBody in sf.json — written as element text content
    if (props.notifications && props.notifications.length) {
      const sfNotifications = moddle.create('sf:Notifications', {
        values: props.notifications.map((n) => {
          const attrs = {};
          if (n.notificationType) attrs.notificationType = n.notificationType;
          if (n.notificationText) attrs.notificationText = n.notificationText;
          return moddle.create('sf:Notification', attrs);
        }),
      });
      extensions.push(sfNotifications);
    }

    // ── sf:SubInfoes ──────────────────────────────────────────────────────
    if (props.subInfoes && props.subInfoes.length) {
      const sfSubInfoes = moddle.create('sf:SubInfoes', {
        values: props.subInfoes.map((s) => {
          const attrs = {};
          for (const key of ['subProcessName', 'subProcessGUID', 'subType', 'subId', 'subVar']) {
            if (s[key] !== undefined && s[key] !== null && s[key] !== '') attrs[key] = s[key];
          }
          return moddle.create('sf:SubInfo', attrs);
        }),
      });
      extensions.push(sfSubInfoes);
    }

    if (extensions.length) {
      if (!el.extensionElements) {
        el.extensionElements = moddle.create('bpmn:ExtensionElements', { values: [] });
      }
      el.extensionElements.values.push(...extensions);
    }
  }

  applyEdgeProperties(el, props, moddle) {
    // ── Direct attrs: guid, from, to ─────────────────────────────────────
    for (const attr of ['guid', 'from', 'to']) {
      if (props[attr] !== undefined && props[attr] !== null && props[attr] !== '') {
        el[attr] = String(props[attr]);
      }
    }

    // ── GroupBehaviours: direct attrs on SequenceFlow via extends ─────────
    // Written as sf:priority="1" sf:forcedMerge="..." etc. — same as sfd-v1
    for (const attr of GROUP_BEHAVIOUR_PROPS) {
      if (props[attr] !== undefined && props[attr] !== null && props[attr] !== '') {
        el[attr] = String(props[attr]);
      }
    }
  }

  /**
   * Writes sf: properties to a bpmn:Process element.
   */
  applyProcessProperties(processEl, processProps, moddle) {
    for (const attr of ['guid', 'code', 'url', 'version']) {
      if (processProps[attr] !== undefined && processProps[attr] !== null && processProps[attr] !== '') {
        processEl[attr] = String(processProps[attr]);
      }
    }
    if (processProps.name) {
      processEl.name = processProps.name;
    }
  }
}
